import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  RefreshCw, 
  Link2, 
  Unlink, 
  DollarSign, 
  CheckCircle2, 
  Clock,
  Building2,
  Search,
  Filter,
  Download,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowRightLeft,
  Receipt,
  TrendingDown
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * How often the page re-reads itself.
 *
 * Transactions land in the portal on PrivatBank's schedule, not on anyone's
 * screen, so this page was only ever as fresh as the last manual F5 — which is
 * exactly what was reported: the sales director kept refreshing by hand to see
 * whether money had arrived. backlog 42d1513f.
 *
 * The backend pull runs hourly too (`bank_sync_loop`); refreshing the page more
 * often than that would only re-read the same rows.
 */
const AUTO_REFRESH_MS = 60 * 60 * 1000;

const BankTransactionsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fops, setFops] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Tax payments state
  const [taxPayments, setTaxPayments] = useState([]);
  const [taxStats, setTaxStats] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxFilter, setTaxFilter] = useState("all"); // all, single_tax, military_tax, social_tax

  // Auto-refresh bookkeeping. Refs, not state: the interval is installed once,
  // but what it has to do depends on filters that change while it runs.
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const totalRef = useRef(0);
  const refreshRef = useRef(() => {});
  const lastRefreshedAtRef = useRef(0);

  // Both the label and the "how stale is this tab" check read the same moment.
  const markRefreshed = () => {
    const now = new Date();
    lastRefreshedAtRef.current = now.getTime();
    setLastRefreshed(now);
  };
  
  // Filters
  const [selectedFop, setSelectedFop] = useState("all");
  const [matchFilter, setMatchFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewMode, setViewMode] = useState("payments"); // "payments" | "internal" | "taxes"
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;
  
  // Matching state
  const [matchingTxId, setMatchingTxId] = useState(null);
  // Payments matched to the picked transaction's payer (EDRPOU / name), loaded
  // on demand when the user clicks Link on a specific row.
  const [matchablePayments, setMatchablePayments] = useState([]);
  const [loadingMatchable, setLoadingMatchable] = useState(false);
  // Manual search fallback when auto-match-by-payer comes up empty / wrong.
  const [matchQuery, setMatchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const openMatch = async (tx) => {
    setMatchingTxId(tx.id);
    setMatchablePayments([]);
    setMatchQuery("");
    setSearchResults([]);
    setLoadingMatchable(true);
    try {
      const res = await api.get(`/bank-transactions/${tx.id}/matchable-payments`);
      setMatchablePayments(res.data.items || []);
    } catch (error) {
      console.error("Error fetching matchable payments:", error);
      toast.error("Не удалось загрузить платежи");
      setMatchablePayments([]);
    } finally {
      setLoadingMatchable(false);
    }
  };

  // Debounced free-text search over all unpaid, unlinked invoices.
  const onMatchQueryChange = (value) => {
    setMatchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = value.trim();
    if (term.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/bank-transactions/search-payments`, { params: { q: term } });
        setSearchResults(res.data.items || []);
      } catch (error) {
        console.error("Error searching payments:", error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const fetchTransactions = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      if (selectedFop !== "all") params.append("fop_id", selectedFop);
      if (matchFilter === "matched") params.append("matched", "true");
      if (matchFilter === "unmatched") params.append("matched", "false");
      if (dateFrom) params.append("start_date", dateFrom);
      if (dateTo) params.append("end_date", dateTo);
      params.append("limit", limit);
      params.append("skip", page * limit);
      
      // Filter by view mode - internal transfers vs regular payments
      if (viewMode === "internal") {
        params.append("only_internal", "true");
      }
      
      const response = await api.get(`/bank-transactions?${params.toString()}`);
      setTransactions(response.data.items || []);
      setTotal(response.data.total || 0);
      totalRef.current = response.data.total || 0;
      markRefreshed();
    } catch (error) {
      console.error("Error fetching transactions:", error);
      // A silent refresh that fails stays silent: the rows on screen are still
      // the last good ones, and an hourly red toast about a blip is noise.
      if (!silent) toast.error("Failed to load transactions");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await api.get("/bank-transactions/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };
  
  const fetchFops = async () => {
    try {
      const response = await api.get("/settings/fops");
      setFops(response.data || []);
    } catch (error) {
      console.error("Error fetching FOPs:", error);
    }
  };
  
  const fetchTaxPayments = async (silent = false) => {
    try {
      if (!silent) setTaxLoading(true);
      const params = new URLSearchParams();
      if (selectedFop !== "all") params.append("fop_id", selectedFop);
      if (taxFilter !== "all") params.append("tax_type", taxFilter);
      if (dateFrom) params.append("start_date", dateFrom);
      if (dateTo) params.append("end_date", dateTo);
      params.append("limit", "100");
      
      const response = await api.get(`/bank-transactions/tax-payments?${params.toString()}`);
      setTaxPayments(response.data.items || []);
      markRefreshed();
    } catch (error) {
      console.error("Error fetching tax payments:", error);
    } finally {
      if (!silent) setTaxLoading(false);
    }
  };
  
  const fetchTaxStats = async () => {
    try {
      const response = await api.get("/bank-transactions/tax-stats");
      setTaxStats(response.data);
    } catch (error) {
      console.error("Error fetching tax stats:", error);
    }
  };
  
  // Rebuilt every render so the interval below always runs against the filters
  // currently on screen.
  refreshRef.current = async () => {
    // Don't pull rows out from under someone mid-action, and don't work in a
    // tab nobody is looking at.
    if (document.hidden || syncing || matchingTxId) return;
    const before = totalRef.current;
    if (viewMode === "taxes") {
      await Promise.all([fetchTaxPayments(true), fetchTaxStats()]);
    } else {
      await Promise.all([fetchTransactions(true), fetchStats()]);
      const added = totalRef.current - before;
      // Only speak up when something actually arrived. An hourly "still
      // nothing" would train people to ignore the toast that matters.
      if (added > 0) {
        toast.success(`Новых транзакций: ${added}`);
      }
    }
  };

  useEffect(() => {
    const tick = () => refreshRef.current();
    const timer = setInterval(tick, AUTO_REFRESH_MS);
    // Coming back to a tab that sat open all morning is the other moment the
    // data on screen is stale.
    const onVisible = () => {
      if (document.hidden) return;
      const age = lastRefreshedAtRef.current ? Date.now() - lastRefreshedAtRef.current : Infinity;
      if (age >= AUTO_REFRESH_MS) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    fetchFops();
    fetchStats();
    fetchTaxStats();
  }, []);
  
  useEffect(() => {
    if (viewMode === "taxes") {
      fetchTaxPayments();
    } else {
      fetchTransactions();
    }
  }, [selectedFop, matchFilter, dateFrom, dateTo, page, viewMode, taxFilter]);
  
  const handleSync = async (period = "week") => {
    try {
      setSyncing(true);
      const params = new URLSearchParams();
      if (selectedFop !== "all") params.append("fop_id", selectedFop);
      
      // Calculate date range based on period
      const today = new Date();
      let startDate;
      
      switch (period) {
        case "today":
          params.append("interim", "true");
          break;
        case "week":
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 7);
          params.append("start_date", startDate.toISOString().split('T')[0]);
          break;
        case "month":
          startDate = new Date(today);
          startDate.setMonth(today.getMonth() - 1);
          params.append("start_date", startDate.toISOString().split('T')[0]);
          break;
        case "year":
          startDate = new Date(today);
          startDate.setFullYear(today.getFullYear() - 1);
          params.append("start_date", startDate.toISOString().split('T')[0]);
          break;
        default:
          break;
      }
      
      const response = await api.post(`/bank-transactions/sync?${params.toString()}`);
      const result = response.data;
      
      if (result.errors && result.errors.length > 0) {
        toast.warning(`Синхронизация с ошибками: ${result.new} новых, ${result.errors.length} ошибок`);
      } else {
        const taxMsg = result.tax_payments ? `, ${result.tax_payments} налоговых` : "";
        toast.success(`Синхронизация завершена: ${result.new} новых транзакций${taxMsg} от ${result.fops_processed} ФОП(ов)`);
      }
      
      fetchTransactions();
      fetchStats();
      fetchTaxPayments();
      fetchTaxStats();
    } catch (error) {
      console.error("Sync error:", error);
      toast.error(error.response?.data?.detail || "Ошибка синхронизации");
    } finally {
      setSyncing(false);
    }
  };
  
  const handleMatch = async (transactionId, paymentId) => {
    try {
      await api.post(`/bank-transactions/${transactionId}/match?payment_id=${paymentId}`);
      toast.success("Transaction matched and payment marked as paid");
      fetchTransactions();
      fetchStats();
      setMatchingTxId(null);
    } catch (error) {
      console.error("Match error:", error);
      toast.error("Failed to match transaction");
    }
  };
  
  const handleUnmatch = async (transactionId) => {
    try {
      await api.delete(`/bank-transactions/${transactionId}/match`);
      toast.success("Match removed");
      fetchTransactions();
      fetchStats();
    } catch (error) {
      console.error("Unmatch error:", error);
      toast.error("Failed to remove match");
    }
  };
  
  // REMOVED 2026-05-22: legacy handleAutoMatch / "Авто-связывание" button.
  // Old endpoint matched by amount+currency only, caused false positives.
  // Auto-linking now happens in-import (PrivatBank sync) using invoice
  // number + amount + currency + counterparty checks. Manual linking
  // remains via Link2 button per row.


  const handleLinkExpense = async (transactionId) => {
    try {
      await api.post(`/bank-transactions/${transactionId}/link-expense`);
      toast.success("Налог добавлен в расходы");
      fetchTaxPayments();
      fetchTaxStats();
    } catch (error) {
      console.error("Link expense error:", error);
      toast.error("Ошибка добавления в расходы");
    }
  };
  
  const handleUnlinkExpense = async (transactionId) => {
    try {
      await api.delete(`/bank-transactions/${transactionId}/unlink-expense`);
      toast.success("Налог убран из расходов");
      fetchTaxPayments();
      fetchTaxStats();
    } catch (error) {
      console.error("Unlink expense error:", error);
      toast.error("Ошибка удаления из расходов");
    }
  };
  
  const filteredTransactions = useMemo(() => {
    if (!searchQuery) return transactions;
    const query = searchQuery.toLowerCase();
    return transactions.filter(tx => 
      tx.counterparty_name?.toLowerCase().includes(query) ||
      tx.purpose?.toLowerCase().includes(query) ||
      tx.counterparty_edrpou?.includes(query) ||
      tx.amount?.toString().includes(query)
    );
  }, [transactions, searchQuery]);
  
  const formatCurrency = (amount, currency = "UAH") => {
    const symbols = { UAH: "₴", USD: "$", EUR: "€" };
    return `${symbols[currency] || currency} ${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Handle both DD.MM.YYYY and YYYY-MM-DD formats
    if (dateStr.includes(".")) {
      const [day, month, year] = dateStr.split(".");
      return `${day}.${month}.${year}`;
    }
    return dateStr;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Building2 className="h-7 w-7 text-blue-400" />
              Банковские транзакции
            </h1>
            <p className="text-zinc-400 text-sm mt-1">Интеграция ПриватБанк — входящие платежи</p>
            <p className="text-zinc-500 text-xs mt-1" data-testid="bank-last-refreshed">
              {lastRefreshed
                ? `Обновлено в ${lastRefreshed.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} · обновляется само раз в час`
                : "Обновляется само раз в час"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleSync("today")}
              disabled={syncing}
              className="border-zinc-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              Сегодня
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSync("week")}
              disabled={syncing}
              className="border-zinc-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              Неделя
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSync("month")}
              disabled={syncing}
              className="border-zinc-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              Месяц
            </Button>
            <Button
              onClick={() => handleSync("year")}
              disabled={syncing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              Год
            </Button>
          </div>
        </div>
        
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-xs">Всего транзакций</p>
                    <p className="text-2xl font-bold text-zinc-50">{stats.total}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-zinc-700" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-900/20 border-green-800/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400/70 text-xs">Связано</p>
                    <p className="text-2xl font-bold text-green-400">{stats.matched}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-700" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-orange-900/20 border-orange-800/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-400/70 text-xs">Не связано</p>
                    <p className="text-2xl font-bold text-orange-400">{stats.unmatched}</p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-700" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div>
                  <p className="text-zinc-500 text-xs mb-2">По валюте</p>
                  <div className="flex gap-3 text-sm">
                    {stats.by_currency?.UAH && (
                      <span className="text-blue-400">₴{stats.by_currency.UAH.total?.toLocaleString()}</span>
                    )}
                    {stats.by_currency?.USD && (
                      <span className="text-green-400">${stats.by_currency.USD.total?.toLocaleString()}</span>
                    )}
                    {stats.by_currency?.EUR && (
                      <span className="text-yellow-400">€{stats.by_currency.EUR.total?.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* View Mode Tabs */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
          <TabsList className="bg-zinc-900/50 border border-zinc-800">
            <TabsTrigger 
              value="payments" 
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Входящие платежи
              {stats && <Badge variant="secondary" className="ml-2 bg-zinc-700">{stats.total}</Badge>}
            </TabsTrigger>
            <TabsTrigger 
              value="internal" 
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50"
            >
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Внутренние переводы
              {stats?.internal_transfers > 0 && (
                <Badge variant="secondary" className="ml-2 bg-zinc-700">{stats.internal_transfers}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="taxes" 
              className="data-[state=active]:bg-red-900 data-[state=active]:text-zinc-50"
            >
              <Receipt className="h-4 w-4 mr-2" />
              Налоговые платежи
              {taxStats?.total_count > 0 && (
                <Badge variant="secondary" className="ml-2 bg-red-800">{taxStats.total_count}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-zinc-500 mb-1 block">Поиск</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    placeholder="Search by name, purpose, EDRPOU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="w-48">
                <label className="text-xs text-zinc-500 mb-1 block">FOP</label>
                <Select value={selectedFop} onValueChange={setSelectedFop}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="All FOPs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All FOPs</SelectItem>
                    {/* Show every active FOP, not only synced ones. Hiding the
                        unconnected ones made a missing PrivatBank token look
                        like lost data ("ФОП Собко Михайло has no transactions").
                        They're listed but flagged as not connected. (3e877536) */}
                    {fops.filter(f => f.is_active !== false).map(fop => (
                      <SelectItem key={fop.id} value={fop.id}>
                        {fop.name}{!fop.privatbank_token ? " — not connected" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Hide match filter for internal transfers */}
              {viewMode === "payments" && (
                <div className="w-40">
                  <label className="text-xs text-zinc-500 mb-1 block">Status</label>
                  <Select value={matchFilter} onValueChange={setMatchFilter}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="matched">Matched</SelectItem>
                      <SelectItem value="unmatched">Unmatched</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Tax type filter for taxes tab */}
              {viewMode === "taxes" && (
                <div className="w-48">
                  <label className="text-xs text-zinc-500 mb-1 block">Тип налога</label>
                  <Select value={taxFilter} onValueChange={setTaxFilter}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все налоги</SelectItem>
                      <SelectItem value="single_tax">Єдиний податок</SelectItem>
                      <SelectItem value="military_tax">Військовий збір</SelectItem>
                      <SelectItem value="social_tax">ЄСВ</SelectItem>
                      <SelectItem value="other_tax">Інші</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="w-36">
                <label className="text-xs text-zinc-500 mb-1 block">From</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="w-36">
                <label className="text-xs text-zinc-500 mb-1 block">To</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Explain an empty register instead of leaving it silently blank: a FOP
            with no Приват24 Автоклієнт token is never synced. (backlog 3e877536) */}
        {(() => {
          const picked = fops.find((f) => f.id === selectedFop);
          if (!picked || picked.privatbank_token) return null;
          return (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-3 text-sm text-amber-200">
                <span className="font-medium">{picked.name}</span> has no PrivatBank
                (Приват24 Автоклієнт) token configured, so its statements are never
                imported — that's why there are no transactions here. Add the token in
                Settings → FOPs to start syncing.
              </CardContent>
            </Card>
          );
        })()}

        {/* Transactions Table */}
        {viewMode !== "taxes" && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {viewMode === "internal" ? "Внутренние переводы (распределительные счета)" : "Входящие платежи"}
              </CardTitle>
              <span className="text-sm text-zinc-500">{total} транзакций</span>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Загрузка...</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                {viewMode === "internal" 
                  ? "Внутренние переводы не найдены."
                  : "Транзакции не найдены. Нажмите \"Синхронизация\" для загрузки из ПриватБанка."
                }
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Дата</TableHead>
                    <TableHead className="text-zinc-400">ФОП</TableHead>
                    <TableHead className="text-zinc-400">Отправитель</TableHead>
                    <TableHead className="text-zinc-400">Назначение</TableHead>
                    <TableHead className="text-zinc-400 text-right">Сумма</TableHead>
                    <TableHead className="text-zinc-400">Связанный платёж</TableHead>
                    <TableHead className="text-zinc-400 text-center">Статус</TableHead>
                    <TableHead className="text-zinc-400 text-center">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map(tx => (
                    <TableRow key={tx.id} className="border-zinc-800/50 hover:bg-zinc-800/30">
                      <TableCell className="text-zinc-50 text-sm">
                        {formatDate(tx.date)}
                      </TableCell>
                      <TableCell>
                        <span className="text-zinc-300 text-sm">{tx.fop_name || "-"}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-zinc-50 text-sm whitespace-normal break-words max-w-[320px]" title={tx.counterparty_name || ""}>{tx.counterparty_name || "-"}</p>
                          {tx.counterparty_edrpou && (
                            <p className="text-zinc-500 text-xs">{tx.counterparty_edrpou}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-zinc-300 text-sm truncate max-w-[200px]" title={tx.purpose}>
                          {tx.purpose || "-"}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono font-medium ${tx.transaction_type === "C" ? "text-green-400" : "text-red-400"}`}>
                          {tx.transaction_type === "C" ? "+" : "-"}{formatCurrency(tx.amount, tx.currency)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {tx.linked_payment_info ? (
                          tx.linked_payment_info.type === "crm" ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">CRM</Badge>
                              <span className="text-zinc-300 text-sm truncate max-w-[150px]">
                                {tx.linked_payment_info.company_name || tx.linked_payment_info.payment_name || "-"}
                              </span>
                            </div>
                          ) : (
                            <a 
                              href={`/sales/project/${tx.linked_payment_info.project_id}`}
                              className="text-cyan-400 hover:text-cyan-300 text-sm truncate max-w-[180px] block hover:underline"
                              title={tx.linked_payment_info.project_name}
                            >
                              {tx.linked_payment_info.project_name || tx.linked_payment_info.payment_name || "-"}
                            </a>
                          )
                        ) : (
                          <span className="text-zinc-500 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {tx.matched_payment_id ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Связано
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                            <Clock className="h-3 w-3 mr-1" />
                            Ожидает
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {tx.matched_payment_id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnmatch(tx.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        ) : (
                          <div className="relative">
                            {matchingTxId === tx.id ? (
                              <div className="flex flex-col gap-1 w-[240px]">
                                <Input
                                  value={matchQuery}
                                  onChange={(e) => onMatchQueryChange(e.target.value)}
                                  placeholder="Поиск счёта по клиенту/№…"
                                  className="h-7 text-xs bg-zinc-800 border-zinc-700 text-zinc-50"
                                />
                                <Select onValueChange={(paymentId) => handleMatch(tx.id, paymentId)}>
                                  <SelectTrigger className="w-[240px] h-8 text-xs bg-zinc-800 border-zinc-700">
                                    <SelectValue placeholder="Выбрать счёт…" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[320px] overflow-y-auto">
                                    {(() => {
                                      const hasQuery = matchQuery.trim().length >= 2;
                                      const list = hasQuery ? searchResults : matchablePayments;
                                      const busy = hasQuery ? searching : loadingMatchable;
                                      if (busy) {
                                        return <div className="p-2 text-xs text-zinc-500">Загрузка…</div>;
                                      }
                                      if (list.length === 0) {
                                        return (
                                          <div className="p-2 text-xs text-zinc-500 max-w-[230px]">
                                            {hasQuery
                                              ? "Ничего не найдено — уточните запрос"
                                              : "Нет счетов этого плательщика. Введите название клиента или № счёта в поиск выше."}
                                          </div>
                                        );
                                      }
                                      return list.map(payment => (
                                        <SelectItem key={payment.id} value={payment.id} className="cursor-pointer">
                                          <div className="flex flex-col py-1">
                                            <span className="text-xs text-zinc-50 flex items-center gap-1">
                                              {payment.project_name || payment.client_name || "Unknown"}
                                              {payment.match_reason === "edrpou" && (
                                                <span className="text-[9px] text-emerald-400 border border-emerald-500/40 rounded px-1">ЕДРПОУ</span>
                                              )}
                                              {payment.invoice_number && (
                                                <span className="text-[9px] text-sky-400 border border-sky-500/40 rounded px-1">№ {payment.invoice_number}</span>
                                              )}
                                            </span>
                                            <span className="text-[10px] text-zinc-400">
                                              {payment.client_name && payment.project_name ? `${payment.client_name} • ` : ""}
                                              {payment.name} • {formatCurrency(payment.amount, payment.currency)}
                                            </span>
                                          </div>
                                        </SelectItem>
                                      ));
                                    })()}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openMatch(tx)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                              >
                                <Link2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                <span className="text-sm text-zinc-500">
                  Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className="border-zinc-700"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                    className="border-zinc-700"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
        
        {/* Tax Payments Table */}
        {viewMode === "taxes" && (
          <>
            {/* Tax Stats Summary */}
            {taxStats && (
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-red-900/20 border-red-800/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-400/70 text-xs">Всего налогов</p>
                        <p className="text-2xl font-bold text-red-400">₴{taxStats.total_amount?.toLocaleString()}</p>
                      </div>
                      <TrendingDown className="h-8 w-8 text-red-700" />
                    </div>
                  </CardContent>
                </Card>
                {taxStats.by_type && Object.entries(taxStats.by_type).map(([type, data]) => (
                  <Card key={type} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <p className="text-zinc-500 text-xs">{type}</p>
                      <p className="text-xl font-bold text-zinc-50">₴{data.amount?.toLocaleString()}</p>
                      <p className="text-zinc-500 text-xs">{data.count} платежей</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-red-400">
                    <Receipt className="h-5 w-5 inline mr-2" />
                    Налоговые платежи
                  </CardTitle>
                  <span className="text-sm text-zinc-500">{taxPayments.length} платежей</span>
                </div>
              </CardHeader>
              <CardContent>
                {taxLoading ? (
                  <div className="text-center py-12 text-zinc-500">Загрузка...</div>
                ) : taxPayments.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Receipt className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Налоговые платежи не найдены.</p>
                    <p className="text-xs mt-2">Синхронизируйте транзакции из ПриватБанка</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400">Дата</TableHead>
                        <TableHead className="text-zinc-400">ФОП</TableHead>
                        <TableHead className="text-zinc-400">Тип налога</TableHead>
                        <TableHead className="text-zinc-400">Получатель</TableHead>
                        <TableHead className="text-zinc-400">Назначение</TableHead>
                        <TableHead className="text-zinc-400">Період</TableHead>
                        <TableHead className="text-zinc-400 text-right">Сумма</TableHead>
                        <TableHead className="text-zinc-400 text-center">В расходах</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taxPayments.map(tx => {
                        const taxLabels = {
                          single_tax: "Єдиний податок",
                          military_tax: "Військовий збір",
                          social_tax: "ЄСВ",
                          other_tax: "Інший"
                        };
                        const taxColors = {
                          single_tax: "bg-blue-900/30 text-blue-400",
                          military_tax: "bg-orange-900/30 text-orange-400",
                          social_tax: "bg-green-900/30 text-green-400",
                          other_tax: "bg-zinc-700 text-zinc-300"
                        };
                        return (
                          <TableRow key={tx.id} className="border-zinc-800/50 hover:bg-zinc-800/30">
                            <TableCell className="text-zinc-50 text-sm">
                              {formatDate(tx.date)}
                            </TableCell>
                            <TableCell>
                              <span className="text-zinc-300 text-sm">{tx.fop_name || "-"}</span>
                            </TableCell>
                            <TableCell>
                              <Badge className={taxColors[tx.tax_type] || taxColors.other_tax}>
                                {taxLabels[tx.tax_type] || tx.tax_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <p className="text-zinc-300 text-sm truncate max-w-[150px]">{tx.counterparty_name || "-"}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-zinc-400 text-xs truncate max-w-[200px]" title={tx.purpose}>
                                {tx.purpose || "-"}
                              </p>
                            </TableCell>
                            <TableCell>
                              {tx.tax_period ? (
                                <Badge variant="outline" className="border-zinc-600 text-zinc-300">
                                  {tx.tax_period}
                                </Badge>
                              ) : (
                                <span className="text-zinc-600">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-mono font-medium text-red-400">
                                -₴{Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              {tx.linked_expense_id ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleUnlinkExpense(tx.id)}
                                  className="text-green-400 hover:text-red-400 hover:bg-red-900/20"
                                  title="Убрать из расходов"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleLinkExpense(tx.id)}
                                  className="text-zinc-500 hover:text-green-400 hover:bg-green-900/20"
                                  title="Добавить в расходы"
                                >
                                  <Link2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default BankTransactionsPage;
